import type { Status } from '../../models/common/Status';
import {
  BatchForm,
  FormAnswerRestrictionLabels,
  FormAnswerRestrictionOption,
  FormAnswerRestrictionsResponse,
  FormTemplate,
  FormType,
  Guid,
  TaskFormItem,
} from '../../models/TaskForm/TaskForm';
export interface TaskFormsResponse {
  items: TaskFormItem[];
  totalItemsCount: number;
}

export interface TaskFormsRequest {
  'Filter.From': string;
  'Filter.To': string;
  PageIndex: number;
  PageSize: number;
  SortKeySelector?: string;
  SortOrder?: string;
  'Filter.ClinicIds'?: string[];
  'Filter.BatchIds'?: string[];
  'Filter.Result'?: string[];
  'Filter.Type'?: FormType;
  'Filter.TemplateIds'?: string[];
  'Filter.TextSearch'?: string;
  'Filter.States'?: string[];
  'Filter.AttemptStates'?: string[];
}

export interface TaskFormTemplatesRequest {
  PageIndex?: number;
  PageSize?: number;
  'Filter.TextSearch'?: string;
  SortKeySelector?: string;
  SortOrder?: string;
}

interface ResponseStatus {
  responseLoading: boolean;
  responseError: string | null;
}

/**
 * Query parameters for GET /api/task/form/report.
 * `From`/`To` are required by the backend; other filters are optional and
 * mirror the journal filter panel so the report matches what the user sees.
 * Note: parameter names here are flat (no `Filter.` prefix) — that's what the
 * endpoint expects per swagger, unlike the journal endpoint.
 */
export interface TaskFormsReportRequest {
  From: string;
  To: string;
  BatchIds?: string[];
  Type?: FormType;
  TemplateIds?: string[];
  ClinicIds?: string[];
  Result?: string[];
  TextSearch?: string;
}

export interface UploadResult {
  total: number;
  uploaded: number;
}

export interface UploadTasksPayload {
  name?: string;
  type: FormType;
  templateId?: string;
  from: string;
  file: FormData;
}

/** Map of form type enum key -> localized label, sourced from the backend. */
export type FormTypeLabels = Partial<Record<FormType, string>>;

export interface FormTypesPayload {
  types: FormType[];
  labels: FormTypeLabels;
}

export interface TaskFormState {
  items: TaskFormItem[];
  totalItemsCount: number;
  batches: BatchForm[];
  templates: FormTemplate[];
  templatesTotalItemsCount: number;
  templatesStatus: ResponseStatus;
  managementTemplates: FormTemplate[];
  managementTemplatesTotalItemsCount: number;
  managementTemplatesStatus: ResponseStatus;
  managementTemplatesRequestId: string | null;
  types: FormType[];
  /**
   * Localized labels for `FormType` keys, returned by `GET /api/task/form/type`.
   * Single source of truth for type captions in tables, filters and forms.
   */
  typeLabels: FormTypeLabels;
  answerRestrictions: FormAnswerRestrictionsResponse;
  answerRestrictionLabels: FormAnswerRestrictionLabels;
  answerRestrictionOptions: FormAnswerRestrictionOption[];
  answerRestrictionsStatus: ResponseStatus;
  userNicknamesById: Record<Guid, string>;
  uploadResult: UploadResult | null;
  status: Status;
}

// Source of truth for available form type *keys* — matches DataAccess.Models.Forms.FormType enum.
// Using the local enum avoids a race condition where /task/form/type returns
// localized labels that cannot be sent back as Filter.Type.
export const FORM_TYPES: FormType[] = Object.values(FormType);

export const initialState: TaskFormState = {
  items: [],
  totalItemsCount: 0,
  batches: [],
  templates: [],
  templatesTotalItemsCount: 0,
  templatesStatus: {
    responseLoading: false,
    responseError: null,
  },
  managementTemplates: [],
  managementTemplatesTotalItemsCount: 0,
  managementTemplatesStatus: {
    responseLoading: false,
    responseError: null,
  },
  managementTemplatesRequestId: null,
  types: FORM_TYPES,
  typeLabels: {},
  answerRestrictions: [],
  answerRestrictionLabels: {},
  answerRestrictionOptions: [],
  answerRestrictionsStatus: {
    responseLoading: false,
    responseError: null,
  },
  userNicknamesById: {},
  uploadResult: null,
  status: {
    responseLoading: false,
    responseError: null,
    requestSuccess: null,
    requestError: null,
  },
};
